import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import AdditionalDocumentsSection from 'views/shared/components/AdditionalDocumentsSection';

import { apiApplicationFormData, apiValidations } from 'api';

import { useAppSelector, useAppDispatch } from 'hooks';
import { useBinderAdditionalFiles } from 'views/shared/hooks/useBinderAdditionalFiles';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import { storeData, resetData } from 'reducers/homePersonalFormReducer';

import SectionMenu from './sections/SectionMenu';
import PolicyInformation from './sections/PolicyInformation';
import LOAInformation from './sections/LOAInformation';
import ApplicantInformation from './sections/ApplicantInformation';
import BrokerInformation from './sections/BrokerInformation';
import PolicyPeriod from './sections/PolicyPeriod';
import Applicants from './sections/Applicants';
import LossHistory from './sections/LossHistory';
import PolicyHistory from './sections/PolicyHistory';
import CrossReferenceInformation from './sections/CrossReferenceInformation';
import Dwellings from './sections/Dwellings';
import MethodOfPayment from './sections/MethodOfPayment';
import BrokerQuestionnaire from './sections/BrokerQuestionnaire';
import Supplements from './sections/Supplements';
import Remarks from './sections/Remarks';
import MissingFields from './sections/MissingFields';
import Submit from './sections/Submit';

import type { APIPersonalHomeApplicationFormData } from 'types/api.types';
import AppealStatusBanner from 'components/AppealStatusBanner';
import BaraReferralRouter from 'components/bara/BaraReferralRouter';
import BaraBrokerStatusBanner from 'components/bara/BaraBrokerStatusBanner';
import BaraFeatureGuard from 'components/bara/BaraFeatureGuard';
import ReferToUnderwriting from 'views/shared/ReferToUnderwriting';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { BaraBillingEditableContext } from 'hooks/useFormBinderDisabled';
import DisclosureAndAttestation from './sections/DisclosureAndAttestation';

const HomeFormView = () => {
  const { uuid } = useParams();
  const dispatch = useAppDispatch();
  const isBaraEnabled = useFeatureFlag('enable-bara');
  const isDataSet = useAppSelector((state) => state.homePersonalForm.isDataSet);

  const {
    error,
    data: initialData,
    isFetching,
  } = useQuery({
    queryKey: ['home_form', uuid],
    queryFn: () => apiApplicationFormData(uuid),
    refetchOnWindowFocus: false,
  }) as {
    error: any;
    data: APIPersonalHomeApplicationFormData;
    isFetching: boolean;
  };

  const {
    additionalFiles,
    isUploadingFile,
    deletingFileId,
    handleFilesUpload,
    handleFileRemove,
  } = useBinderAdditionalFiles(uuid || '', initialData?.additional_files);

  const { data: validations, error: validationsError } = useQuery({
    queryKey: [`validations_${uuid}`],
    queryFn: () => apiValidations(uuid),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    dispatch(resetData());
  }, []);

  useEffect(() => {
    if (isDataSet || isFetching || !initialData?.data) return;

    dispatch(
      storeData({
        uuid,
        data: initialData.data,
        remarks: initialData.remarks,
        files: initialData.files,
        payments: initialData.payments,
        binder_pdf: initialData.binder_pdf,
        signing: initialData.signing,
        appeal: initialData.appeal,
        bara_referral: initialData.bara_referral ?? null,
        wip_status: initialData.wip_status,
        supplements: initialData.supplements,
      }),
    );
  }, [initialData, uuid, isDataSet, isFetching, dispatch]);

  if (!isDataSet) {
    return <Card textAlign="center">Preparing form...</Card>;
  }

  if (validationsError) {
    const errorData = (validationsError as any)?.error;
    const errorMessage =
      errorData?.[0]?.quote || errorData?.[0] || 'Failed to load validations';
    return (
      <Card textAlign="center">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Validation Error</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      </Card>
    );
  }

  if (!validations) {
    return <Card textAlign="center">Loading validations...</Card>;
  }

  if (error) {
    return (
      <Card textAlign="center">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{JSON.stringify(error)}</AlertDescription>
        </Alert>
      </Card>
    );
  }

  return (
    <ApplicationFormContext.Provider
      value={{ validations: validations?.validations || [] }}
    >
      <BaraFeatureGuard silent>
        <BaraBrokerStatusBanner formStateKey="homePersonalForm" />
      </BaraFeatureGuard>
      <SectionMenu />
      <PolicyInformation uploadLOA={initialData?.is_loa} />
      <LOAInformation />
      <ApplicantInformation />
      <BrokerInformation />
      <PolicyPeriod />
      <Applicants />
      <LossHistory />
      <PolicyHistory />
      <CrossReferenceInformation />
      <Dwellings />
      <BaraBillingEditableContext.Provider value>
        <MethodOfPayment />
      </BaraBillingEditableContext.Provider>
      <BrokerQuestionnaire />
      <Supplements />
      <Remarks />
      <DisclosureAndAttestation />

      <AdditionalDocumentsSection
        uploadedFiles={additionalFiles}
        onFilesUpload={handleFilesUpload}
        onFileRemove={handleFileRemove}
        isUploading={isUploadingFile}
        isDeletingFileId={deletingFileId}
        formStateKey="homePersonalForm"
      />

      {isBaraEnabled ? (
        <BaraReferralRouter formStateKey="homePersonalForm" />
      ) : (
        <ReferToUnderwriting formStateKey="homePersonalForm" />
      )}
      <AppealStatusBanner formStateKey="homePersonalForm" />
      <MissingFields />
      <Submit />
    </ApplicationFormContext.Provider>
  );
};

export default HomeFormView;
